/**
 * Settings → API access. Where a partner finds the REST API documentation, and mints the
 * client_secret their own systems sign in with.
 *
 * THE SCREEN IS SHAPED BY ONE FACT: a client_secret is shown exactly once. The server keeps only a
 * one-way hash of it (server/src/oauth/clientCredentials/), so there is no "show it again" and never
 * will be. Everything here follows from that:
 *
 *   • Generating is a TWO-STEP act. The first click only opens the warning, because the thing being
 *     warned about — every system still configured with the old secret stops being able to sign in —
 *     happens the instant the request lands, and cannot be undone by generating again.
 *   • The new secret appears in a panel that will not close on its own, with a Copy button, and says
 *     plainly that this is the only time anyone will see it. Nothing on the page re-fetches while it
 *     is open; the only way past it is to dismiss it deliberately.
 *   • Afterwards the card reports WHEN the live secret was set and when it was last used, and no part
 *     of the secret itself — not even a prefix. Those two dates are what an owner part-way through a
 *     rotation actually needs ("is the value in my config the current one?"), and they answer it
 *     without the server keeping any fragment of the credential to answer it with.
 *
 * The documentation button opens the API's own Swagger UI in a new tab rather than embedding it: the
 * page's Try-it-out makes real requests, which only work without CORS trouble from the API's own
 * origin, and a partner reading reference material wants it in a tab they can keep.
 */
import React, { useEffect, useState } from 'react';
import { KeyRound, ExternalLink, Copy, Check, AlertTriangle, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import {
    apiDocsUrl,
    getApiCredential,
    generateClientSecret,
    revokeClientSecret,
    explainCredentialError,
    type ApiCredential,
} from '../../services/apiCredentialsApi.ts';

// Matched to the other cards in this settings column (FirmLicensingSettings, FirmTeamSettings).
const card = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    marginTop: 14,
    overflow: 'hidden',
} as const;
const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 8,
    padding: '9px 14px',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
} as const;
const primaryBtn = { ...btnBase, background: '#1a6fd4', color: '#fff', border: 0 } as const;
const outlineBtn = { ...btnBase, background: '#fff', color: '#1a6fd4', border: '1.5px solid #1a6fd4' } as const;
const ghostBtn = { ...btnBase, background: '#f1f5f9', color: '#475569', border: 0 } as const;
const dangerBtn = { ...btnBase, background: '#fff', color: '#b91c1c', border: '1.5px solid #fca5a5' } as const;
const mono = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12.5,
    color: '#0f172a',
    wordBreak: 'break-all',
} as const;
const fieldLabel = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase',
    color: '#64748b',
} as const;

/** "4 Oct 2026", or a dash when there is no date to show. */
function onDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface CopyButtonProps {
    value: string;
    label: string;
    style?: React.CSSProperties;
}

/** Copy, and say so for a moment. Silent when the browser blocks the clipboard — the value is on
 *  screen in full precisely so it can still be selected by hand. */
function CopyButton({ value, label, style }: CopyButtonProps) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* Clipboard unavailable (permissions, insecure context) — nothing to recover from. */
        }
    };
    return (
        <button type="button" onClick={() => void copy()} style={{ ...ghostBtn, ...style }} aria-label={label}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
}

type Stage = 'idle' | 'confirming' | 'revealing';

export function ApiAccessSettings() {
    const [credential, setCredential] = useState<ApiCredential | null>(null);
    const [loading, setLoading] = useState(true);
    const [stage, setStage] = useState<Stage>('idle');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    /** The new secret, in this component's state and nowhere else, until the panel is dismissed. */
    const [secret, setSecret] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const current = await getApiCredential();
                if (!cancelled) setCredential(current);
            } catch (err) {
                if (!cancelled) setError(explainCredentialError(err, 'Could not read your API credential just now.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const generate = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const minted = await generateClientSecret();
            setSecret(minted.clientSecret);
            setCredential(minted.credential);
            setStage('revealing');
        } catch (err) {
            setError(explainCredentialError(err, 'Could not generate a client secret just now.'));
            setStage('idle');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const { revoked } = await revokeClientSecret();
            // Re-read rather than patch: the card's job is to report what the server holds, and a
            // locally-assumed state is how a screen starts disagreeing with the thing it describes.
            setCredential(await getApiCredential());
            setNotice(revoked ? 'That client secret no longer works.' : 'There was no live client secret to revoke.');
        } catch (err) {
            setError(explainCredentialError(err, 'Could not revoke your client secret just now.'));
        } finally {
            setBusy(false);
        }
    };

    /** Clear the secret from state the moment the panel closes. Nothing else holds a copy. */
    const dismissSecret = () => {
        setSecret(null);
        setStage('idle');
    };

    return (
        <div style={card}>
            <div style={{ padding: '18px 20px 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <KeyRound size={16} color="#1a6fd4" />
                    {/* A real heading, styled to sit flush with the sibling cards' plain titles: the
                        settings column is long enough that a screen reader needs the landmarks. */}
                    <h3 style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', margin: 0 }}>API access</h3>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55, margin: 0 }}>
                    Call AgedCareWise from your own systems — your CRM, a nightly job, anything that can make an HTTPS
                    request. A client secret signs in as you and carries exactly the access you have, and everything done
                    with it is recorded against you on the audit trail.
                </p>
            </div>

            <div style={{ padding: '14px 20px 20px' }}>
                <button
                    type="button"
                    style={outlineBtn}
                    onClick={() => window.open(apiDocsUrl(), '_blank', 'noopener,noreferrer')}
                >
                    <ExternalLink size={14} /> Open API documentation
                </button>
                <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5, margin: '9px 0 0' }}>
                    Every endpoint, with a <strong>Try it out</strong> button on each one. Paste an access token into{' '}
                    <strong>Authorize</strong> at the top and the page will use it for every call you make there.
                </p>

                <div style={{ height: 1, background: '#f1f5f9', margin: '18px 0' }} />

                {loading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
                        <Loader2 size={15} className="animate-spin" /> Loading your credentials…
                    </div>
                )}

                {!loading && !credential && stage === 'idle' && (
                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, margin: '0 0 14px' }}>
                        You haven&apos;t generated a client secret yet. Generating one gives you a client ID and a secret to
                        put into whichever system needs to sign in.
                    </p>
                )}

                {!loading && credential && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={fieldLabel}>Client ID</div>
                                <div style={{ ...mono, marginTop: 3 }}>{credential.clientId}</div>
                            </div>
                            <CopyButton value={credential.clientId} label="Copy client ID" />
                        </div>
                        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, margin: '7px 0 0' }}>
                            Not a secret, and it never changes — generating a new secret leaves it alone. Sending it when you
                            sign in is optional, and worth doing: it ties your session to this one client, so a token that
                            escapes your integration cannot be used anywhere else.
                        </p>

                        <div style={{ height: 1, background: '#e2e8f0', margin: '13px 0' }} />

                        <div style={fieldLabel}>Client secret</div>
                        {credential.hasSecret ? (
                            <>
                                {/* No part of the secret is shown — nothing readable of it is kept.
                                    These two dates are what tell an owner mid-rotation whether the
                                    value in their configuration is still the live one. */}
                                <div style={{ fontSize: 13.5, color: '#0f172a', marginTop: 3 }}>
                                    Set {onDate(credential.secretSetAt)}
                                </div>
                                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 4, lineHeight: 1.55 }}>
                                    {credential.lastUsedAt
                                        ? `Last used ${onDate(credential.lastUsedAt)}`
                                        : 'Never used yet'}
                                </div>
                            </>
                        ) : (
                            <div style={{ fontSize: 13, color: '#b45309', marginTop: 4 }}>
                                No live secret — nothing can sign in with this client ID until you generate one.
                            </div>
                        )}
                    </div>
                )}

                {/* The reveal. Deliberately the only thing on offer while it is open. */}
                {stage === 'revealing' && secret && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 12, padding: '15px 16px', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                            <AlertTriangle size={16} color="#b45309" />
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>
                                Copy this now — it will not be shown again
                            </div>
                        </div>
                        <p style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.55, margin: '0 0 11px' }}>
                            This is the only time anyone sees this secret. We store a one-way hash of it, so neither you nor
                            we can read it back — if it is lost, the only way forward is to generate another one. Keep it
                            somewhere a secret belongs: a password manager or your deployment&apos;s secret store, never in
                            source control or an email.
                        </p>
                        <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 8, padding: '11px 12px' }}>
                            <code style={{ ...mono, display: 'block' }}>{secret}</code>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                            <CopyButton
                                value={secret}
                                label="Copy client secret"
                                style={{ background: '#b45309', color: '#fff' }}
                            />
                            <button type="button" style={ghostBtn} onClick={dismissSecret}>
                                I&apos;ve saved it
                            </button>
                        </div>
                    </div>
                )}

                {/* The warning. Shown BEFORE anything is generated, because nothing here is reversible. */}
                {stage === 'confirming' && (
                    <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '15px 16px', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                            <AlertTriangle size={16} color="#b91c1c" />
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#991b1b' }}>
                                {credential?.hasSecret ? 'This will break anything using your current secret' : 'Before you generate'}
                            </div>
                        </div>
                        <p style={{ fontSize: 12.5, color: '#991b1b', lineHeight: 1.6, margin: 0 }}>
                            {credential?.hasSecret ? (
                                <>
                                    Generating a new client secret <strong>invalidates the one you have now, immediately</strong>.
                                    Every system still configured with the old secret will be refused when it next tries to
                                    sign in — so be ready to put the new value into each of them. Access tokens already issued
                                    keep working until they expire, and your client ID does not change.
                                </>
                            ) : (
                                <>
                                    You will be shown the new secret once, and once only. Have somewhere safe to put it before
                                    you continue.
                                </>
                            )}
                        </p>
                        <div style={{ display: 'flex', gap: 10, marginTop: 13, flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                style={{ ...primaryBtn, background: '#b91c1c', opacity: busy ? 0.7 : 1 }}
                                disabled={busy}
                                onClick={() => void generate()}
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                                {busy ? 'Generating…' : 'Generate the new secret'}
                            </button>
                            <button type="button" style={ghostBtn} disabled={busy} onClick={() => setStage('idle')}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'idle' && !loading && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button type="button" style={primaryBtn} disabled={busy} onClick={() => setStage('confirming')}>
                            {credential?.hasSecret ? <RefreshCw size={14} /> : <KeyRound size={14} />}
                            {credential?.hasSecret ? 'Generate a new client secret' : 'Generate a client secret'}
                        </button>
                        {credential?.hasSecret && (
                            <button type="button" style={dangerBtn} disabled={busy} onClick={() => void revoke()}>
                                <Trash2 size={14} /> Revoke
                            </button>
                        )}
                    </div>
                )}

                {error && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#b91c1c', lineHeight: 1.5 }}>{error}</div>
                )}
                {notice && !error && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#047857', lineHeight: 1.5 }}>{notice}</div>
                )}
            </div>
        </div>
    );
}
